// Insurance eligibility check 接口层（FRONTEND_INTEGRATION 文档）。
// 鉴权走 provider session（schedule-token 自动注入）；insuranceId/clientId 后端强校验归属。
// 出参在这里就地解包 R<> 并（必要时）经 adapt 转前端契约，调用方拿到的即展示模型。
import { Request } from 'api/request';
import { getUserId } from 'utils/localstore';
import {
    TBackendEligibilityCheckVo,
    TEligibilityInvoiceDetailVo,
    TEligibilityInvoiceListItem,
    TEligibilityInvoiceListReq,
    TEligibilityInvoiceSummaryVo,
    TEligibilityNotificationType,
    TEligibilityPrecheckVo,
    TEligibilitySetupReq,
    TIPage,
    TInsurancePayerVo,
    TPayerIdEntry,
    TPracticeProvider,
    TProviderInsurancePayerReq,
    TRecheckAppointmentVo,
    TProviderNotificationItemVo,
    TProviderPayerConfigVo,
    TEligibilityCheckHistoryRow,
} from 'types/insuranceEligibility';
import { adaptHistoryRows, withZone } from './adapt';

const ELIG = '/schedule-server/prospect/eligibility';
const INVOICES = '/schedule-server/provider/insurance-billing/eligibility/invoices';
const PAYER_CONFIG = '/schedule-server/provider/insurance/payer-config';

// ---------- Prospect eligibility check（detail 页模块） ----------
// practice 视图的动作全部以归属 provider 身份执行（OVERRIDE-PROVIDER-ID；请求层对 self 自动不发头）：
// 计费/日限/payer 配置都按被查 provider 口径，操作人由后端 session 另记（trigger_user_id 不被覆盖）

// 前置检查：三个 flag 各对应一种 setup gap（org 开关 / billing NPI / payer ID 映射）
export const getEligibilityPrecheck = async (
    insuranceId: number,
    overrideProviderId?: number,
): Promise<TEligibilityPrecheckVo | undefined> => {
    const res: any = await Request.post({
        url: `${ELIG}/precheck/${insuranceId}`,
        disableErrorNote: true,
        overrideProviderId,
    });
    return res?.data?.data;
};

// 触发 270/271 check（每次都计费，含缓存命中——已与产品拍板）。返回后端原始 Vo，由调用方走 adapt
export const runEligibilityCheck = async (
    insuranceId: number,
    overrideProviderId?: number,
): Promise<TBackendEligibilityCheckVo | undefined> => {
    const res: any = await Request.post({ url: `${ELIG}/check/${insuranceId}`, overrideProviderId });
    return res?.data?.data;
};

// To-do "Insurance to re-check" 入口的 check：键是 appointment 而不是保险行——后端按 appointmentId
// 定位患者，把该 (client, provider) 下所有 NEED_PROVIDER_RECHECK 的行各跑一次同样的 270。
// 回的是本次处理的行数，不是 check 结果（要看结果得重拉详情 / history）；不计入每日限额（todayCheckCount 不算它），也不会被限额拒绝
export const runEligibilityRecheck = async (appointmentId: number): Promise<number | undefined> => {
    const res: any = await Request.post({ url: `${ELIG}/recheck/${appointmentId}` });
    // 后端合法地回 0（名下已无待复查行）；请求失败时回 undefined，调用方据此区分
    return res?.data?.data;
};

// 触发 payer discovery（$1.50，人人收费）。前置：latestStatus == UNMAPPED
export const runEligibilityDiscovery = async (
    insuranceId: number,
    overrideProviderId?: number,
): Promise<TBackendEligibilityCheckVo | undefined> => {
    const res: any = await Request.post({ url: `${ELIG}/discovery/${insuranceId}`, overrideProviderId });
    return res?.data?.data;
};

// Request updated insurance（保险卡更新请求）：SMS / EMAIL / MESSAGE 三路 fan-out + patient 侧红点。
// practice 视图带 override → in-app 卡片/红点以归属 provider 名义发
export const requestUpdatedInsurance = async (
    clientId: number,
    params: { messageToPatient: string; channels: Array<'SMS' | 'EMAIL' | 'MESSAGE'> },
    overrideProviderId?: number,
): Promise<boolean> => {
    const res: any = await Request.post({
        url: `${ELIG}/request-updated-insurance/${clientId}`,
        params,
        overrideProviderId,
    });
    return !!res?.data?.data;
};

// 一次性 setup：保存 billing NPI（练所级）并打开 org 级 eligibility 开关。
// payer ID 的增量保存走 payer-config（3.2）；本接口只在 NPI 步出现过的首次 setup 调用
export const saveEligibilitySetup = async (params: TEligibilitySetupReq): Promise<boolean> => {
    const res: any = await Request.post({ url: `${ELIG}/setup`, params });
    return !!res?.data?.data;
};

// To-do "Insurance to re-check" 列表（§2.7，只读）：一次全量，后端已按 startAt ASC 排。
// Check CTA 只负责跳转客户详情页（不自动触发 check——已拍板）；check 完成后后端清 action-state
export const getRecheckAppointments = async (): Promise<TRecheckAppointmentVo[]> => {
    const res: any = await Request.get({ url: `${ELIG}/recheck-appointments`, disableErrorNote: true });
    return res?.data?.data || [];
};

// ---------- check history（标题行 History 弹窗，§2.8） ----------

// (当前 provider, 当前 patient) 这一对的 attempt 流水，check_at DESC；分页 size 后端 clamp 到 [1,50]。
// practice 视图带 OVERRIDE-PROVIDER-ID：口径跟着被查 provider 走，与该页其他动作一致
export const getEligibilityCheckHistory = async (
    clientId: number,
    page: { current: number; size: number },
    overrideProviderId?: number,
): Promise<{ rows: TEligibilityCheckHistoryRow[]; total: number }> => {
    const res: any = await Request.post({
        url: `${ELIG}/check/history/${clientId}`,
        params: { current: page.current, size: page.size },
        overrideProviderId,
    });
    // PageBaseResp 在本仓库有两种字段拼法（list/totalRecords 与 records/total），两种都收
    const data = res?.data?.data;
    return {
        rows: adaptHistoryRows(data?.list || data?.records),
        total: data?.totalRecords ?? data?.total ?? 0,
    };
};

// ---------- Provider notification（红点通知，§8）----------

// items/{type} 只返回未读条目；sourceId 可选（PATIENT_INSURANCE_UPDATED → prospectId，
// GROUP_MEMBER_PAYER_ID_SET_UP → 新 provider.id）。payer-config 保存成功后后端自动按
// provider 置读（3.2 副作用），前端只需重查，无需调 read 接口
export const getNotificationItems = async (
    type: TEligibilityNotificationType,
    sourceId?: number,
): Promise<TProviderNotificationItemVo[]> => {
    const res: any = await Request.get({
        url: `/schedule-server/provider/notification/state/items/${type}`,
        params: { sourceId },
        disableErrorNote: true,
    });
    const items: TProviderNotificationItemVo[] = res?.data?.data || [];
    // createAt 是裸 UTC（LocalDateTime）。CardUpdateNote 既拿它跟 latestCheckedAt 比大小、又拿它渲染
    // 日期，两处都要求真时刻，所以在边界上补齐，调用方不必知道这件事
    return items.map((it) => ({ ...it, createAt: withZone(it.createAt) }));
};

// ---------- payer ID / NPI 设置流 ----------

const toEntry = (vo: TInsurancePayerVo): TPayerIdEntry => ({
    carrier: vo.carrier,
    state: vo.state,
    plans: vo.plans || [],
    commercialPayerId: vo.commercialPayerId,
    governmentPayerId: vo.governmentPayerId,
});

// 设置弹窗一次拿全：grid 骨架（provider × state × carrier）来自 payer-config（group owner 拿全
// org active provider，其余只拿自己）；chip 圆点/横幅由通知 items 驱动（getNotificationItems）
export const getPayerIdSetup = async (): Promise<{
    providers: TPracticeProvider[];
    entries: Record<number, TPayerIdEntry[]>;
}> => {
    const res: any = await Request.get({ url: PAYER_CONFIG });
    const list: TProviderPayerConfigVo[] = res?.data?.data || [];
    const selfId = Number(getUserId());
    const entries: Record<number, TPayerIdEntry[]> = {};
    const providers: TPracticeProvider[] = list.map((vo) => {
        const rows = (vo.insurances || []).map(toEntry);
        entries[vo.providerId] = rows;
        return {
            providerId: vo.providerId,
            name: `${vo.firstName || ''} ${vo.lastName || ''}`.trim() || `Provider ${vo.providerId}`,
            states: Array.from(new Set(rows.map((r) => r.state))),
            self: list.length === 1 || vo.providerId === selfId,
        };
    });
    return { providers, entries };
};

// 保存某 provider 的 payer 映射：3.2 是整份全量替换（漏传条目会被删），必须传该 provider 的完整列表。
// 写侧无 plans 字段，与前端一致：plan 只读展示（2026-09-08 拍板），本接口只写 payer ID
export const savePayerEntries = async (providerId: number, entries: TPayerIdEntry[]): Promise<boolean> => {
    const params: TProviderInsurancePayerReq[] = entries.map((e) => ({
        providerId,
        state: e.state,
        carrier: e.carrier,
        commercialPayerId: e.commercialPayerId || undefined,
        governmentPayerId: e.governmentPayerId || undefined,
    }));
    const res: any = await Request.post({ url: PAYER_CONFIG, params });
    return !!res?.data?.data;
};

// NPI registry（NPPES）查询不再单独封装：NpiSetupModal 直接复用 insurance billing onboarding 的
// NpiRegistryBox（内部走 api/insuranceBilling 的 lookupNpi），查询/结果渲染都在组件里

// ---------- Financials → Eligibility invoices ----------

export const getEligibilityInvoiceSummary = async (
    timeRangeMonths?: number,
): Promise<TEligibilityInvoiceSummaryVo | undefined> => {
    const res: any = await Request.get({ url: `${INVOICES}/summary`, params: { timeRangeMonths } });
    return res?.data?.data;
};

export const getEligibilityInvoices = async (
    params: TEligibilityInvoiceListReq,
): Promise<TIPage<TEligibilityInvoiceListItem> | undefined> => {
    const res: any = await Request.get({ url: INVOICES, params });
    return res?.data?.data;
};

export const getEligibilityInvoiceDetail = async (
    invoiceId: number,
): Promise<TEligibilityInvoiceDetailVo | undefined> => {
    const res: any = await Request.get({ url: `${INVOICES}/${invoiceId}` });
    return res?.data?.data;
};

// 虚拟 INVOICE_PENDING 行的详情：当月未出账的 billable 调用现算（结构同 §1.3，invoiceId/issue/due 为 null）。
// practice 发票视图看别的 provider 的 pending 行时带 override（后端已确认认头）；个人视图不传
export const getUnbilledEligibilityInvoice = async (
    overrideProviderId?: number,
): Promise<TEligibilityInvoiceDetailVo | undefined> => {
    const res: any = await Request.get({ url: `${INVOICES}/unbilled`, overrideProviderId });
    return res?.data?.data;
};

// ---- practice 视图（§1.6/1.7）：owner/admin 跨 provider 聚合。发票仍是 per-provider × per-month，
// 这里只是混排 + 汇总；每 provider 一张 Stripe invoice（都扣 owner 的卡），行级状态/PDF 独立 ----

export const getPracticeEligibilityInvoiceSummary = async (
    timeRangeMonths?: number,
): Promise<TEligibilityInvoiceSummaryVo | undefined> => {
    const res: any = await Request.get({ url: `${INVOICES}/practice/summary`, params: { timeRangeMonths } });
    return res?.data?.data;
};

// 注意 practice 列表是 POST + body（个人版是 GET + query）；行带 provider 身份五字段
export const getPracticeEligibilityInvoices = async (
    params: TEligibilityInvoiceListReq,
): Promise<TIPage<TEligibilityInvoiceListItem> | undefined> => {
    const res: any = await Request.post({ url: `${INVOICES}/practice/list`, params });
    return res?.data?.data;
};

// PDF 走 Stripe 托管发票（与 managed billing fee 等其他 fee 账单同一套）；billingId = 列表行 invoiceId。
// Stripe invoice 尚未生成时返回 null → 调用方提示 not available；INVOICE_PENDING 行（invoiceId=null）不渲染入口
export const getEligibilityInvoicePdf = async (invoiceId: number): Promise<string | undefined> => {
    const res: any = await Request.get({
        url: `/schedule-server/provider/stripe/invoice/${invoiceId}/pdf-url`,
        disableErrorNote: true,
    });
    return res?.data?.data || undefined;
};
